// Abrangencia (coverage.name) e texto livre: o admin grava o endereco do Google
// ("Belo Horizonte, MG, Brasil") e a API V2 grava o que o parceiro envia
// ("BELO HORIZONTE, MG", "Belo Horizonte/MG, MG"). Mesma regra do CoverageNameHelper do back-end.

const UFS = new Set([
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
]);
const COUNTRIES = new Set(["brasil", "brazil"]);
const LOWERCASE_WORDS = new Set(["de", "da", "do", "das", "dos", "e", "d'"]);
const CITY_WITH_UF = /^(.+?)\s*[/-]\s*([A-Za-z]{2})$/;

const removeAccents = (text) => text.normalize("NFD").replace(/[̀-ͯ]/g, "");
const isUf = (text) => UFS.has((text || "").toUpperCase());
const isAllUpper = (text) => !!text && text !== text.toLowerCase() && text === text.toUpperCase();
const hasAccent = (text) => !!text && removeAccents(text) !== text;

function toTitleCase(text) {
  return text
    .toLocaleLowerCase("pt-BR")
    .split(" ")
    .map((word, index) =>
      index > 0 && LOWERCASE_WORDS.has(word)
        ? word
        : word
            .split("-")
            .map((part) => (part ? part.charAt(0).toLocaleUpperCase("pt-BR") + part.slice(1) : part))
            .join("-")
    )
    .join(" ");
}

export function parseCoverage(name) {
  if (!name || !name.trim()) return { city: null, uf: null };

  const parts = name
    .split(",")
    .map((part) => part.trim().replace(/\s+/g, " "))
    .filter((part) => part.length > 0);

  if (parts.length > 1 && COUNTRIES.has(parts[parts.length - 1].toLowerCase())) parts.pop();

  let uf = null;
  if (parts.length > 1 && isUf(parts[parts.length - 1])) uf = parts.pop().toUpperCase();

  let city = parts.length > 0 ? parts[parts.length - 1] : null;
  if (city) {
    const match = city.match(CITY_WITH_UF);
    if (match && isUf(match[2])) {
      city = match[1].trim();
      if (!uf) uf = match[2].toUpperCase();
    }
  }

  return { city, uf };
}

export function coverageKey(name) {
  const { city, uf } = parseCoverage(name);
  if (!city) return "";
  return `${removeAccents(city).toLowerCase()}|${uf || ""}`;
}

export function canonicalCoverageName(name) {
  if (!name || !name.trim()) return name;
  let { city, uf } = parseCoverage(name);
  if (!city) return name.trim();
  if (isAllUpper(city)) city = toTitleCase(city);
  return uf ? `${city}, ${uf}` : city;
}

export function bestCoverageDisplayName(names) {
  const candidates = names.filter((name) => name && name.trim());
  candidates.sort((a, b) => {
    const cityA = parseCoverage(a).city;
    const cityB = parseCoverage(b).city;
    const accent = Number(hasAccent(cityB)) - Number(hasAccent(cityA));
    if (accent !== 0) return accent;
    return Number(isAllUpper(cityA)) - Number(isAllUpper(cityB));
  });
  return canonicalCoverageName(candidates[0]);
}

// Uma entrada por localidade, com a melhor grafia, em ordem alfabetica.
export function uniqueCoverages(coverages) {
  const groups = new Map();
  coverages
    .filter((coverage) => coverage && coverage.name && coverage.name.trim())
    .forEach((coverage) => {
      const key = coverageKey(coverage.name);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(coverage);
    });

  return Array.from(groups.values())
    .map((group) => ({ ...group[0], name: bestCoverageDisplayName(group.map((coverage) => coverage.name)) }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}
